import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { getAllClientTypes } from "../Api/getAllClientTypes";
import { getClientCampaigns } from "../Api/campaigns";
import type { ICampaign, IClientType } from "../Types/account";
import type { IUser } from "../Types/user";
import { getUserFromLS } from "../utils/getUserFromLS";
import {
  canSwitchWorkspaceClient,
  getAccessibleClients,
  getClientDisplayName,
  getPrimaryClientId,
  getWorkspaceMode,
  isTutorialWorkspaceClientId,
  readStoredWorkspaceClientId,
  resolveEffectiveClientId,
  resolveInitialWorkspaceClientId,
  toClientTypeOptions,
  writeStoredWorkspaceClientId,
} from "../utils/workspaceClientHelpers";
import {
  readStoredWorkspaceCampaignId,
  resolveWorkspaceCampaign,
  writeStoredWorkspaceCampaignId,
} from "../utils/workspaceCampaignHelpers";
import { isTutorialFixtureActive } from "../utils/tutorialFixtureState";

export interface WorkspaceClientContextValue {
  isPlatformAdmin: boolean;
  canSwitchClient: boolean;
  selectedClientId: string | null;
  effectiveClientId: string | null;
  setSelectedClientId: (
    clientId: string | null,
    options?: { persist?: boolean },
  ) => void;
  clearSelectedClient: () => void;
  clients: IClientType[];
  clientsLoading: boolean;
  refreshClients: () => void;
  clientDisplayName: string;
  campaigns: ICampaign[];
  campaignsLoading: boolean;
  campaignsError: string | null;
  selectedCampaignId: string | null;
  selectedCampaign: ICampaign | null;
  setSelectedCampaignId: (campaignId: string) => void;
  refreshCampaigns: () => void;
}

const WorkspaceClientContext = createContext<WorkspaceClientContextValue | undefined>(
  undefined
);

const noopSetSelectedClientId = () => {};
const noopClearSelectedClient = () => {};
const noopSetSelectedCampaignId = () => {};
const noopRefreshCampaigns = () => {};
const noopRefreshClients = () => {};

const INERT_VALUE: WorkspaceClientContextValue = {
  isPlatformAdmin: false,
  canSwitchClient: false,
  selectedClientId: null,
  effectiveClientId: null,
  setSelectedClientId: noopSetSelectedClientId,
  clearSelectedClient: noopClearSelectedClient,
  clients: [],
  clientsLoading: false,
  refreshClients: noopRefreshClients,
  clientDisplayName: "",
  campaigns: [],
  campaignsLoading: false,
  campaignsError: null,
  selectedCampaignId: null,
  selectedCampaign: null,
  setSelectedCampaignId: noopSetSelectedCampaignId,
  refreshCampaigns: noopRefreshCampaigns,
};

interface WorkspaceClientProviderProps {
  children: ReactNode;
}

function computeSyncSelectedClientId(
  user: IUser | undefined,
  isAuthenticated: boolean
): string | null {
  if (!isAuthenticated || !user?.id) return null;
  const mode = getWorkspaceMode(user);
  if (mode === "platformAdmin") {
    return readStoredWorkspaceClientId(user.id);
  }
  const accessible = getAccessibleClients(user);
  const ids = accessible.map((c) => String(c.id));
  const stored = readStoredWorkspaceClientId(user.id);
  return resolveInitialWorkspaceClientId(user, stored, ids);
}

function computeSyncClients(
  user: IUser | undefined,
  isAuthenticated: boolean
): IClientType[] {
  if (!isAuthenticated || !user?.id) return [];
  if (getWorkspaceMode(user) === "platformAdmin") return [];
  return toClientTypeOptions(getAccessibleClients(user));
}

export const WorkspaceClientProvider: React.FC<WorkspaceClientProviderProps> = ({
  children,
}) => {
  const isAuthenticated = useSelector(
    (state: { auth: { isAuthenticated: boolean } }) => state.auth.isAuthenticated
  );
  const authUser = useSelector(
    (state: { auth: { user?: IUser } }) => state.auth.user
  );
  const storedUser = useMemo(() => getUserFromLS(), [isAuthenticated]);
  // Sign-in stores the login response, then `/users/me` replaces it moments
  // later with the record that carries `client` and `accessible_clients`.
  // Reading localStorage only when `isAuthenticated` flips would pin the whole
  // first session to that partial payload, and a missing `client` reads as
  // platform admin — which offers the org switcher to users who have no second
  // org to switch to.
  const candidateUser = authUser?.access_token ? authUser : storedUser;
  // Every `/users/me` refresh (billing polls included) yields a new object.
  // Key off the fields this provider actually reads so an unrelated refresh
  // can't re-run the effects below and clobber the selected client.
  const workspaceIdentity = JSON.stringify({
    id: candidateUser?.id ?? null,
    role: candidateUser?.role ?? null,
    primaryClientId: getPrimaryClientId(candidateUser),
    accessibleClientIds: getAccessibleClients(candidateUser)
      .map((client) => String(client.id))
      .sort(),
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const user = useMemo(() => candidateUser, [workspaceIdentity]);

  const workspaceMode = useMemo(
    () => (isAuthenticated && user?.id ? getWorkspaceMode(user) : "singleClient"),
    [isAuthenticated, user]
  );
  const isPlatformAdmin = workspaceMode === "platformAdmin";
  const canSwitchClient =
    isAuthenticated && user?.id ? canSwitchWorkspaceClient(user) : false;

  const [selectedClientId, setSelectedClientIdState] = useState<string | null>(() =>
    computeSyncSelectedClientId(user, isAuthenticated)
  );
  const [clients, setClients] = useState<IClientType[]>(() =>
    computeSyncClients(user, isAuthenticated)
  );
  const [clientsLoading, setClientsLoading] = useState(
    () => isAuthenticated && !!user?.id && getWorkspaceMode(user) === "platformAdmin"
  );
  const [clientRefreshToken, setClientRefreshToken] = useState(0);
  const clientsLoadedRef = useRef(false);
  const campaignRequestRef = useRef(0);
  const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
  const [campaignRequestLoading, setCampaignsLoading] = useState(false);
  const [campaignsClientId, setCampaignsClientId] = useState<string | null>(null);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [selectedCampaignId, setSelectedCampaignIdState] = useState<string | null>(
    null,
  );
  const [campaignRefreshToken, setCampaignRefreshToken] = useState(0);

  const persistSelection = useCallback(
    (clientId: string | null) => {
      if (!user?.id || !canSwitchClient) return;
      if (workspaceMode === "platformAdmin") {
        writeStoredWorkspaceClientId(user.id, clientId);
        return;
      }
      if (workspaceMode === "multiClient" && clientId) {
        writeStoredWorkspaceClientId(user.id, clientId);
      }
    },
    [user?.id, canSwitchClient, workspaceMode]
  );

  const setSelectedClientId = useCallback(
    (clientId: string | null, options?: { persist?: boolean }) => {
      if (!canSwitchClient) return;
      setSelectedClientIdState(clientId);
      if (options?.persist === false) return;
      persistSelection(clientId);
    },
    [canSwitchClient, persistSelection]
  );

  const clearSelectedClient = useCallback(() => {
    if (!canSwitchClient) return;
    setSelectedClientIdState(null);
    if (user?.id) {
      writeStoredWorkspaceClientId(user.id, null);
    }
  }, [canSwitchClient, user?.id]);

  // Platform admin: fetch all client types
  useEffect(() => {
    if (!isAuthenticated || !isPlatformAdmin) {
      if (!isPlatformAdmin) {
        clientsLoadedRef.current = false;
      }
      return;
    }

    setClientsLoading(true);
    if (clientsLoadedRef.current) return;
    clientsLoadedRef.current = true;

    getAllClientTypes(
      (resp) => {
        setClients(resp);
        setClientsLoading(false);
        const stored = readStoredWorkspaceClientId(user.id);
        const ids = resp.map((c) => String(c.id));
        const initial = resolveInitialWorkspaceClientId(user, stored, ids);
        setSelectedClientIdState((current) => {
          // Don't clobber an in-progress App Guide selection, and never leave
          // a stale tutorial client id after clients finish loading.
          if (current && ids.includes(current)) {
            if (
              isTutorialWorkspaceClientId(current) &&
              !isTutorialFixtureActive()
            ) {
              return initial;
            }
            return current;
          }
          return initial;
        });
      },
      (err) => {
        console.error("Error fetching client types:", err);
        setClientsLoading(false);
        clientsLoadedRef.current = false;
      }
    );
  }, [clientRefreshToken, isAuthenticated, isPlatformAdmin, user.id]);

  // Multi-client / single-client: derive from accessible_clients on user
  useEffect(() => {
    if (!isAuthenticated || isPlatformAdmin) return;

    const accessible = getAccessibleClients(user);
    const clientOptions = toClientTypeOptions(accessible);
    setClients(clientOptions);
    setClientsLoading(false);

    const ids = clientOptions.map((c) => String(c.id));
    const stored = readStoredWorkspaceClientId(user.id);
    const initial = resolveInitialWorkspaceClientId(user, stored, ids);
    setSelectedClientIdState(initial);
    if (workspaceMode === "multiClient" && initial) {
      writeStoredWorkspaceClientId(user.id, initial);
    }
  }, [isAuthenticated, isPlatformAdmin, user, workspaceMode]);

  // Reset on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedClientIdState(null);
      setClients([]);
      setClientsLoading(false);
      clientsLoadedRef.current = false;
    }
  }, [isAuthenticated]);

  // Scrub a leftover App Guide client id from a prior broken tour exit.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (isTutorialFixtureActive()) return;
    const stored = readStoredWorkspaceClientId(user.id);
    if (!isTutorialWorkspaceClientId(stored)) return;
    const availableIds = clients.map((c) => String(c.id));
    const fallback = resolveInitialWorkspaceClientId(user, null, availableIds);
    writeStoredWorkspaceClientId(user.id, fallback);
    setSelectedClientIdState((current) =>
      isTutorialWorkspaceClientId(current) ? fallback : current,
    );
  }, [isAuthenticated, user, clients]);

  const effectiveClientId = useMemo(() => {
    if (!isAuthenticated || !user?.id) return null;
    if (isPlatformAdmin) {
      return selectedClientId;
    }
    return resolveEffectiveClientId(selectedClientId, user);
  }, [isAuthenticated, user, isPlatformAdmin, selectedClientId]);

  // A new client is unready in its first render, before effects reset state.
  const campaignsLoading = campaignRequestLoading ||
    (Boolean(effectiveClientId) && campaignsClientId !== effectiveClientId);

  const clientDisplayName = useMemo(
    () => getClientDisplayName(effectiveClientId, clients, user),
    [effectiveClientId, clients, user]
  );

  useEffect(() => {
    const requestId = ++campaignRequestRef.current;
    setCampaignsClientId(effectiveClientId);
    if (!isAuthenticated || !user?.id || !effectiveClientId) {
      setCampaigns([]);
      setSelectedCampaignIdState(null);
      setCampaignsError(null);
      setCampaignsLoading(false);
      return;
    }

    setCampaigns([]);
    setSelectedCampaignIdState(null);
    setCampaignsError(null);
    setCampaignsLoading(true);

    void getClientCampaigns(effectiveClientId)
      .then((response) => {
        if (campaignRequestRef.current !== requestId) return;
        const resolved = resolveWorkspaceCampaign(
          response,
          readStoredWorkspaceCampaignId(user.id, effectiveClientId),
        );
        setCampaigns(response);
        setSelectedCampaignIdState(resolved?.id ?? null);
        if (resolved) {
          writeStoredWorkspaceCampaignId(user.id, effectiveClientId, resolved.id);
        }
      })
      .catch((error) => {
        if (campaignRequestRef.current !== requestId) return;
        console.error("Failed to load campaigns:", error);
        setCampaigns([]);
        setSelectedCampaignIdState(null);
        setCampaignsError("Campaigns could not be loaded.");
      })
      .finally(() => {
        if (campaignRequestRef.current === requestId) {
          setCampaignsLoading(false);
        }
      });

    // Invalidate the request on unmount so a response that lands afterwards
    // cannot touch state belonging to a provider that no longer exists.
    return () => {
      campaignRequestRef.current += 1;
    };
  }, [
    campaignRefreshToken,
    effectiveClientId,
    isAuthenticated,
    user?.id,
  ]);

  const setSelectedCampaignId = useCallback(
    (campaignId: string) => {
      if (!effectiveClientId || !user?.id) return;
      if (!campaigns.some((campaign) => campaign.id === campaignId)) return;
      setSelectedCampaignIdState(campaignId);
      writeStoredWorkspaceCampaignId(user.id, effectiveClientId, campaignId);
    },
    [campaigns, effectiveClientId, user?.id],
  );

  const refreshCampaigns = useCallback(() => {
    setCampaignRefreshToken((token) => token + 1);
  }, []);

  const refreshClients = useCallback(() => {
    if (!isPlatformAdmin) return;
    clientsLoadedRef.current = false;
    setClientRefreshToken((token) => token + 1);
  }, [isPlatformAdmin]);

  const selectedCampaign = useMemo(
    () =>
      campaigns.find((campaign) => campaign.id === selectedCampaignId) ?? null,
    [campaigns, selectedCampaignId],
  );

  const value = useMemo<WorkspaceClientContextValue>(() => {
    if (!isAuthenticated || !user?.id) {
      return INERT_VALUE;
    }

    if (!canSwitchClient && workspaceMode === "singleClient") {
      const primaryId = resolveEffectiveClientId(null, user);
      const accessible = getAccessibleClients(user);
      const singleClients = toClientTypeOptions(accessible);
      return {
        isPlatformAdmin: false,
        canSwitchClient: false,
        selectedClientId: primaryId,
        effectiveClientId: primaryId,
        setSelectedClientId: noopSetSelectedClientId,
        clearSelectedClient: noopClearSelectedClient,
        clients: singleClients,
        clientsLoading: false,
        refreshClients: noopRefreshClients,
        clientDisplayName: getClientDisplayName(primaryId, singleClients, user),
        campaigns,
        campaignsLoading,
        campaignsError,
        selectedCampaignId,
        selectedCampaign,
        setSelectedCampaignId,
        refreshCampaigns,
      };
    }

    return {
      isPlatformAdmin,
      canSwitchClient,
      selectedClientId,
      effectiveClientId,
      setSelectedClientId,
      clearSelectedClient,
      clients,
      clientsLoading,
      refreshClients,
      clientDisplayName,
      campaigns,
      campaignsLoading,
      campaignsError,
      selectedCampaignId,
      selectedCampaign,
      setSelectedCampaignId,
      refreshCampaigns,
    };
  }, [
    isAuthenticated,
    user,
    canSwitchClient,
    workspaceMode,
    isPlatformAdmin,
    selectedClientId,
    effectiveClientId,
    setSelectedClientId,
    clearSelectedClient,
    clients,
    clientsLoading,
    refreshClients,
    clientDisplayName,
    campaigns,
    campaignsLoading,
    campaignsError,
    selectedCampaignId,
    selectedCampaign,
    setSelectedCampaignId,
    refreshCampaigns,
  ]);

  return (
    <WorkspaceClientContext.Provider value={value}>
      {children}
    </WorkspaceClientContext.Provider>
  );
};

export function useWorkspaceClient(): WorkspaceClientContextValue {
  const ctx = useContext(WorkspaceClientContext);
  if (ctx === undefined) {
    throw new Error("useWorkspaceClient must be used within a WorkspaceClientProvider");
  }
  return ctx;
}

/** @deprecated Use useWorkspaceClient */
export function useAdminClient(): WorkspaceClientContextValue {
  return useWorkspaceClient();
}

/** @deprecated Use WorkspaceClientProvider */
export const AdminClientProvider = WorkspaceClientProvider;

export type AdminClientContextValue = WorkspaceClientContextValue;
